// @ts-strict-ignore
import { MultiSelect, Tooltip } from '@usealloy/component-library';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef,
} from 'react';

import type { AttributeComponent, AttributeComponentDefinition } from 'flow/store/engine/types';
import {
  updateDataRouteValue,
  updateThreshold,
} from 'flow/store/redux/slice';
import { useAppDispatch } from '~/store/reduxHooks';

import { getMultiSelectProps } from '../MLAComponentDropdown/helpers';
import type { DropdownOption } from '../../NodeEditorDrawer/types';
import { MLAAttributeItemOrigin } from '../../NodeEditorDrawer/types';
import { MLAEditorContext } from '../context';
import { mlaCTACaretPositionString } from '../../useCaretControl/constants';
import { generateMLAComponentCaret } from '~/dashboard/views/workflows/service/mla';
import { getMLAHtmlElementId } from 'flow/editor/hooks/useWorkflowHistory/helpers';
import { UiEffectHighlighter } from 'flow/editor/hooks/useWorkflowHistory/styles';
import { useMlaHistoryDispatch } from '../useMlaHistoryDispatch';

function AddAttributeFiltersDropdown({
  componentPath,
  mlaData,
}: {
  componentPath: string;
  mlaData: AttributeComponent[];
}) {
  const appDispatch = useAppDispatch();

  const multiSelectRef = useRef(null);

  const {
    sourceData,
    morphingCaretPosition,
    getNewPositionToJump,
    setPendingMorphingCaretPositionUpdateValue,
    setMorphingCaretPosition,
  } = useContext(MLAEditorContext);

  const uiEffectElementId = useMemo(() => getMLAHtmlElementId('filters'), []);
  const handleDispatch = useMlaHistoryDispatch(uiEffectElementId);

  const optionalAttrComponentsWithoutValue = useMemo(() => (
    mlaData.filter(attr => attr.attributeComponent.isOptional && !attr.value)
  ), [mlaData]);

  const options = React.useMemo<DropdownOption[]>(() => (
    optionalAttrComponentsWithoutValue.map(({ attributeComponent }) => (
      {
        value: attributeComponent.placeholder,
        label: attributeComponent.label || attributeComponent.placeholder,
      }
    ))
  ), [optionalAttrComponentsWithoutValue]);

  const selectedOption = useMemo(() => ({
    value: 'add_filter',
    label: 'Add Filter',
  }), []);

  const getDefaultInitialValue = (
    attributeComponent: AttributeComponentDefinition,
  ): string | number | (string | number)[] => {
    switch (attributeComponent.dataType) {
      case 'decimal':
      case 'select_one':
      case 'integer':
      case 'boolean':
        return attributeComponent.default;
      case 'multi_select':
        return [attributeComponent.default];
      case 'csv':
        return [];
      default:
        return '';
    }
  };

  const onAddFilter = useCallback(inputValue => {
    const { value } = inputValue;
    if (value === 'add_filter') return; // This option is a placeholder, so we shouldn't do anything.

    const indexOfSelectedAttrComponent = mlaData.findIndex(
      attr => attr.attributeComponent.placeholder === value,
    );

    const initialDefaultValue = getDefaultInitialValue(
      mlaData[indexOfSelectedAttrComponent].attributeComponent,
    );

    const payload = {
      path: `${componentPath}[${indexOfSelectedAttrComponent}].value`,
      value: initialDefaultValue,
    };

    if (sourceData.type === MLAAttributeItemOrigin.DATAROUTE) {
      handleDispatch(() => appDispatch(updateDataRouteValue({ ...payload })));
    } else {
      handleDispatch(() => appDispatch(updateThreshold({ ...payload })));
    }

    const newFilterCaret = generateMLAComponentCaret({
      filterComponent: {
        ...mlaData[indexOfSelectedAttrComponent],
        value: undefined,
      },
    });
    const { newPositionString, newPositionIndexForDelayedUpdate } = getNewPositionToJump({
      userActionObject: {
        mode: 'add_filter',
        value: newFilterCaret,
      },
    });

    if (newPositionIndexForDelayedUpdate) {
      setPendingMorphingCaretPositionUpdateValue(newPositionIndexForDelayedUpdate);
    } else {
      setMorphingCaretPosition(newPositionString);
    }
  }, [
    mlaData, componentPath, sourceData, getNewPositionToJump,
    appDispatch, setPendingMorphingCaretPositionUpdateValue, setMorphingCaretPosition,
    handleDispatch,
  ]);

  const noFiltersAvailable = optionalAttrComponentsWithoutValue.length === 0;

  const selectProps = useMemo(() => getMultiSelectProps(
    undefined,
    noFiltersAvailable,
  ), [noFiltersAvailable]);

  useEffect(() => {
    if (morphingCaretPosition === mlaCTACaretPositionString) {
      // focus the dropdown automatically when caret position is pointing here
      multiSelectRef?.current?.focus();
    }
  }, [morphingCaretPosition]);

  const onEscKey = () => {
    multiSelectRef?.current?.blur();
  };

  return (
    <Tooltip
      align="start"
      content="No filters available"
      style={{
        wordBreak: 'break-all',
      }}
      side="top"
      variant="primary"
      hide={!noFiltersAvailable}
    >
      <UiEffectHighlighter
        data-testid="MLAFilterDropdown"
        id={uiEffectElementId}
        tabIndex={-1}
      >
        <MultiSelect
          {...selectProps}
          openMenuOnFocus
          onChange={onAddFilter}
          options={options}
          isDisabled={noFiltersAvailable}
          selectedOptions={selectedOption}
          placeholder="Add Filters"
          onKeyDownHandlers={{
            Esc: onEscKey,
          }}
          ref={multiSelectRef}
        />
      </UiEffectHighlighter>
    </Tooltip>
  );
}

export default AddAttributeFiltersDropdown;
